import { parseCivilDate } from '@/shared/lib/utils';
import type {
  ArchivingModel,
  ArchivingRoute,
  ArchivingRouteDto,
  ArchivingSegment,
  DatePart,
} from '../model/archiving.types';

export const MAX_ARCHIVING_ROUTES = 4;

export const ARCHIVING_ROOT_FOLDER = 'Arquivo';

export const ARCHIVING_MODEL_OPTIONS: { id: ArchivingModel; name: string }[] = [
  { id: 'ARCHIVE_YEAR', name: 'Arquivo / Ano' },
  { id: 'ARCHIVE_MONTH', name: 'Arquivo / Ano / Mês' },
  { id: 'ARCHIVE_GENERIC', name: 'Arquivo / Ano / Opções genéricas' },
];

const DATE_PART_TOKEN_SUFFIX: Record<DatePart, string> = {
  year: ':ano',
  month: ':mês',
};

const MONTH_FORMATTER = new Intl.DateTimeFormat('pt-BR', { month: 'long' });

export function getRouteLabel(index: number): string {
  return `${index + 1}ª Rota`;
}

export function getMonthName(monthIndex: number): string {
  const name = MONTH_FORMATTER.format(new Date(2000, monthIndex, 1));
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function toToken(segment: ArchivingSegment): string {
  const suffix = segment.datePart ? DATE_PART_TOKEN_SUFFIX[segment.datePart] : '';
  return `{${segment.fieldName}${suffix}}`;
}

function joinPath(parts: string[]): string {
  return parts.length === 0 ? '' : `/${[ARCHIVING_ROOT_FOLDER, ...parts].join('/')}/`;
}

function filledSegments(segments: ReadonlyArray<ArchivingSegment>): ArchivingSegment[] {
  return segments.filter((segment) => segment.fieldName !== '');
}

export function formatDatePartExample(civilDate: string, datePart: DatePart): string | null {
  const date = parseCivilDate(civilDate);
  if (!date) return null;

  return datePart === 'year' ? String(date.getFullYear()) : getMonthName(date.getMonth());
}

export function buildArchivingSegments(
  model: ArchivingModel | null,
  routes: ReadonlyArray<ArchivingRoute>
): ArchivingSegment[] {
  const dateFieldName = routes[0]?.fieldName ?? '';
  if (model === null || dateFieldName === '') return [];

  const year: ArchivingSegment = { fieldName: dateFieldName, datePart: 'year' };

  if (model === 'ARCHIVE_YEAR') return [year];
  if (model === 'ARCHIVE_MONTH') {
    return [year, { fieldName: dateFieldName, datePart: 'month' }];
  }

  return [year, ...routes.slice(1).map((route) => ({ fieldName: route.fieldName }))];
}

// The backend refuses routes without a model and reads position 1 as the date route, so without
// a date field there is nothing valid to send: a generic route promoted to position 1 would only
// come back as a type error.
export function buildArchivingRoutesPayload(
  model: ArchivingModel | null,
  routes: ReadonlyArray<{ fieldName: string }>
): ArchivingRouteDto[] {
  const [first, ...rest] = routes;
  if (model === null || first === undefined || first.fieldName === '') return [];

  const generic = model === 'ARCHIVE_GENERIC' ? rest.slice(0, MAX_ARCHIVING_ROUTES - 1) : [];

  return [first, ...generic]
    .filter((route) => route.fieldName !== '')
    .map((route, index) => ({ position: index + 1, fieldName: route.fieldName }));
}

export function routesFromPayload(dtos: ReadonlyArray<ArchivingRouteDto>): string[] {
  const ordered = [...dtos].sort((a, b) => a.position - b.position);
  const names = ordered.map((dto) => dto.fieldName);

  return ordered[0]?.position === 1 ? names : ['', ...names];
}

export function buildTokenPath(segments: ReadonlyArray<ArchivingSegment>): string {
  return joinPath(filledSegments(segments).map(toToken));
}

export function buildExamplePath(
  segments: ReadonlyArray<ArchivingSegment>,
  exampleValues: Readonly<Record<string, string>>
): string {
  const parts = filledSegments(segments).map((segment) => {
    const value = exampleValues[segment.fieldName];
    if (value === undefined) return toToken(segment);
    if (!segment.datePart) return value;
    return formatDatePartExample(value, segment.datePart) ?? toToken(segment);
  });

  return joinPath(parts);
}
